import React from "react";
import { useAuthState } from "../../../components/AuthProvider";
import { useParams, Link, useHistory, useLocation } from "react-router-dom";
import Container from "../../../components/Container";
import Flex from "../../../components/Flex";
import Layout from "../../../components/Layout";
import SearchRecordTable from "../../NewSearchRecords/SearchRecordTable";
import handleResponse from "../../../utils/handleFetchResponse";
import fhirBundleParser from "../../../utils/fhirBundleParser";
import Spinner from "../../../components/Loader/Spinner";
import Breadcrumbs from "../../../components/Breadcrumbs";
import fetchConfig from "../../../utils/fetchConfig";
import { useMessageToast } from "../../../components/MessageProvider";

import styles from "./ReviewQueue.module.scss";

const ReviewQueueTable = () => {
  const location = useLocation();
  const { name } = location.state || null;

  const reviewQueueType = {
    CollisionDetectionReviewQueue: "Collision Review Queue",
    NearMissReviewQueue: "Near Misses",
    TwinsReviewQueue: "Twins",
    IMDMNearMatchReviewQueue: "Possible Over Merge",
    OverlayReviewQueue: "Overlay Review Queue",
    UploadableReviewQueue: `Uploadable Review Queue:  ${name}`
  };

  const { user } = useAuthState();
  const toast = useMessageToast();
  const history = useHistory();
  const { category } = location.state || "assignedReviewQueue";
  const {
    selectedClient: { oid: clientId }
  } = user.preferences;
  const { rqType } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [rqFhirBundleData, setRqFhirBundleData] = React.useState([]);
  const [aggregate, setAggregate] = React.useState(null);
  const [gid, setGid] = React.useState("");
  const [riskScore, setRiskScore] = React.useState("");
  const [clientSubscriptions, setClientSubscriptions] = React.useState(
    new Map()
  );

  const fetchClientSubscriptions = React.useCallback(async () => {
    const url = `${process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN}/client_subscriptions/?client__oid=${clientId}`;
    const mockUrl = `/clientSubscription.json`;
    const config = {
      method: "GET"
    };
    fetch(...fetchConfig(url, mockUrl, config))
      .then(handleResponse)
      .then(response => {
        const clientMap = new Map();
        clientMap.set(clientId, [
          response.results[0].review_queues,
          response.results[0].record_combiner
        ]);
        setClientSubscriptions(clientMap);
      })
      .catch(error => {
        toast.error("Failed to fetch client subscription");
      });
  }, [clientId, toast]);

  const fetchReviewQueues = React.useCallback(() => {
    setLoading(true);
    const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/reviewqueue`;
    const mockUrl = `/fetchReviewQueue1.json`;
    const config = {
      credentials: "include",
      headers: {
        clientId,
        userId: user.username,
        rqType: rqType,
        category: category,
        "Content-Type": "application/json"
      }
    };
    if (category === "assignedReviewQueue") {
      config.headers.rq_name = name;
    }
    fetch(...fetchConfig(url, mockUrl, config))
      .then(handleResponse)
      .then(response => {
        setLoading(false);
        let data = fhirBundleParser(response.data);
        if (!data[0]) {
          history.goBack();
        }
        setRqFhirBundleData(data);
        setGid(data[0] ? data[0].id : "");
        fetchAggregate(data[0] ? data[0].id : "");
        let riskScoreUrl =
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/imdm/#eiia-risk-score";
        if (data[0] && data[0].extension) {
          for (let i = 0; i < data[0].extension.length; i++) {
            if (riskScoreUrl === data[0].extension[i].url)
              setRiskScore(data[0].extension[i].valueString);
          }
        } else {
          setRiskScore("");
        }
      })
      .catch(e => {
        setGid("");
        setLoading(false);
      });
  }, [rqType, user.username, category, name, clientId]);

  const fetchAggregate = React.useCallback(
    goldenId => {
      const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/aggregate/${goldenId}`;
      const mockUrl = `/getIdentityDetails.json`;
      const config = {
        headers: {
          clientId,
          userId: user.username,
          "Content-Type": "application/json",
          "source-of-request": "HCM Admin UI"
        }
      };
      fetch(...fetchConfig(url, mockUrl, config))
        .then(handleResponse)
        .then(response => {
          setAggregate(response.data);
        })
        .catch(e => {
          console.error("fetchAggregate error; ", e);
        });
    },
    [clientId]
  );

  const skipReviewedHandler = status => {
    const org_name = user.preferences.selectedClient.organization.name;
    setLoading(true);
    const url = `${process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN}/rpc/reviewQueueItems/update/?client_id=${clientId}&golden_id=${gid}&rq_status=${status}&category=${category}&org_name=${org_name}&risk_score=${riskScore}`;
    const config = {
      credentials: "include",
      method: "PUT"
    };
    let headers = {
      user_id: user.username
    };

    if (category === "assignedReviewQueue") {
      headers.rqname = name;
    }

    config.headers = headers;
    fetch(url, config)
      .then(handleResponse)
      .then(() => {
        setLoading(false);
        fetchReviewQueues();
      })
      .catch(error => {
        setLoading(false);
        toast.error(
          `Failed to ${
            status === "Reviewed" ? "review" : "skip"
          } item in the review queue`
        );
      });
  };

  const resetToInitialState = () => {
    window.scrollTo(0, 0);
    setClientSubscriptions(new Map());
    setGid("");
  };

  React.useEffect(() => {
    if (clientSubscriptions.has(clientId) && !loading) {
      fetchReviewQueues();
    }
  }, [clientId, fetchReviewQueues, clientSubscriptions]);

  React.useEffect(() => {
    resetToInitialState();
  }, [clientId]);

  React.useEffect(() => {
    if (!clientSubscriptions.has(clientId)) {
      fetchClientSubscriptions();
    }
  }, [clientSubscriptions, fetchClientSubscriptions, clientId]);

  React.useEffect(() => {
    console.log(clientId);

    console.log(clientSubscriptions.get(clientId));
    if (
      clientSubscriptions.has(clientId) &&
      !clientSubscriptions.get(clientId)[0].find(({ type }) => type === rqType)
    ) {
      history.push("/review-queue-dashboard");
    }
  }, [clientSubscriptions, clientId, rqType, history]);

  return (
    <Layout>
      <Container>
        <div className={styles.base}>
          <Flex gutter={3}>
            <Flex.Item span={12}>
              <Breadcrumbs>
                <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
                <Breadcrumbs.Item
                  render={() => (
                    <Link to="/review-queue-dashboard">Review Queue</Link>
                  )}
                />
                <Breadcrumbs.Item last render={() => reviewQueueType[rqType]} />
              </Breadcrumbs>
            </Flex.Item>
            {loading || !clientSubscriptions.has(clientId) ? (
              <div style={{ height: "500px" }} className={styles.loader}>
                <Spinner />
              </div>
            ) : (
              gid && (
                <SearchRecordTable
                  rq_gid={gid}
                  rq_aggregate={aggregate}
                  is_review_queue_page={true}
                  skipReviewedHandler={skipReviewedHandler}
                  rq_clientId={clientId}
                  rqFhirBundleData={rqFhirBundleData}
                />
              )
            )}
          </Flex>
        </div>
      </Container>
    </Layout>
  );
};

export default ReviewQueueTable;
